import { getTrack, getSelectedTrack } from '../api.js';
import { setNavigationGuard, clearNavigationGuard } from '../router.js';
import { mountEditor } from '../../../editor/src/main.js';
import { densifyClosedLoop } from '../../../shared/spline.js';
import { newEmptyTrack } from '../../../shared/track-schema.js';
import { fmtLong, fmtShort, fmtSpeed, M_TO_FT, FT_TO_M, MS_TO_MPH } from '../../../shared/units.js';

// Round-trip the in-memory editor state across the edit/race route
// boundary so "Test track" preserves unsaved edits.
//
// Primary bridge is a module-scoped reference (no size limit, no
// serialization cost — same-tab navigation doesn't reload the JS).
// sessionStorage is best-effort secondary so a page reload mid-race
// can still resume the session — it's per-origin-quota-limited
// (5–10 MB), and tracks with embedded base64 backgrounds usually
// blow past that. Failures there are caught and ignored.
export const EDITOR_SESSION_KEY = 'race-manager:editor-session';
let editorSessionMem = null;

export function setEditorSessionTrack(track) {
  editorSessionMem = track;
  try {
    sessionStorage.setItem(EDITOR_SESSION_KEY, JSON.stringify(track));
  } catch (_) { /* quota exceeded — in-memory still works */ }
}

export function getEditorSessionTrack() {
  if (editorSessionMem) return editorSessionMem;
  try {
    const json = sessionStorage.getItem(EDITOR_SESSION_KEY);
    if (json) return JSON.parse(json);
  } catch (_) {}
  return null;
}

const MODES = [
  { id: 'track',      label: 'Track' },
  { id: 'walls',      label: 'Walls' },
  { id: 'surfaces',   label: 'Surfaces' },
  { id: 'markers',    label: 'Markers' },
  { id: 'pitlane',    label: 'Pit' },
  { id: 'racingline', label: 'Line' },
  { id: 'labels',     label: 'Labels' },
];
const FULLY_WIRED_MODES = new Set(['track', 'walls', 'surfaces', 'markers', 'pitlane', 'racingline', 'labels']);

// HTML-attribute escape — only used when interpolating user-typed
// strings (label text, area names) into innerHTML attribute slots.
function escapeAttr(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
const MARKER_LABELS = { startFinish: 'S/F', startLine: 'Start line', sector0: 'Sector 1', sector1: 'Sector 2' };
const SURFACE_TYPES = ['grass', 'gravel', 'sand', 'tarmac', 'curb'];
const CURB_COLOR_KEYS = ['red', 'yellow', 'blue', 'grey'];
const CURB_COLOR_HEX = { red: '#e54b3c', yellow: '#f5c441', blue: '#3a7ad6', grey: '#8c93a0' };
const TARMAC_COLOR_KEYS = ['default', 'light', 'dark', 'blue', 'red'];
const TARMAC_COLOR_HEX = {
  default: '#272b28',
  light:   '#3a3f3c',
  dark:    '#1a1d1b',
  blue:    '#1f2a3a',
  red:     '#3a1f1f',
};

// Curb band thickness limits — real F1 curbs are roughly 60–90 cm wide;
// our authoring slider goes 2 ft (≈0.6 m) to 8 ft (≈2.4 m), default 5 ft
// (≈1.524 m). Stored in SI; the UI converts when imperial is selected.
const CURB_THICKNESS_MIN_M     = 0.6096; // 2 ft
const CURB_THICKNESS_MAX_M     = 2.4384; // 8 ft
const CURB_THICKNESS_DEFAULT_M = 1.524;  // 5 ft

const isMacUA = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
const cmdKey = isMacUA ? '⌘' : 'Ctrl';
// Alt on PC, Option on Mac. Both fire e.altKey === true in the
// browser, so the binding is platform-agnostic; only the label differs.
const altKey = isMacUA ? '⌥' : 'Alt';

// Display unit. Stored in localStorage under the same key as the
// standalone editor so the preference travels between them.
const UNIT_STORAGE_KEY = 'trackEditorUnits';
function readStoredUnit() {
  if (typeof localStorage === 'undefined') return 'imperial';
  const v = localStorage.getItem(UNIT_STORAGE_KEY);
  return v === 'metric' || v === 'imperial' ? v : 'imperial';
}
let UNIT = readStoredUnit();

// Width slider config — values stored in SI (meters). Display as feet
// when UNIT === 'imperial'.
const WIDTH_M_MIN = 6;     // 20 ft
const WIDTH_M_MAX = 30;    // ~100 ft
const WIDTH_M_STEP = 0.5;
const ALPHA_MIN = 0;
const ALPHA_MAX = 1;
const ALPHA_STEP = 0.05;

export async function render(mountEl, params) {
  // Selected track id lives in sessionStorage so the URL stays clean
  // (`#/edit` instead of `#/edit?track=<id>`). The `?source=session`
  // flag is independent — when set, we ignore the selected track and
  // load the editor's own staged session track instead (Test Track
  // round-trip from the race page). No selection + no source =
  // blank-canvas "+ New track" flow.
  const trackId = getSelectedTrack();

  mountEl.innerHTML = `
    <div class="edit-toolbar">
      <div class="edit-toolbar-left">
        <span class="edit-title" id="edit-title">Loading…</span>
        <span class="edit-mod-indicator" id="edit-mod-indicator" hidden>● modified</span>
      </div>
      <div class="edit-toolbar-center" id="edit-modes">
        ${MODES.map(m => `
          <button class="pill" data-mode="${m.id}" title="${m.label} mode${FULLY_WIRED_MODES.has(m.id) ? '' : ' (editing coming in phase 4c)'}">
            ${m.label.toUpperCase()}
          </button>
        `).join('')}
      </div>
      <div class="edit-toolbar-right">
        <div class="unit-toggle seg-toggle" id="unit-toggle" title="Display units">
          <button data-unit="imperial">FT</button>
          <button data-unit="metric">M</button>
        </div>
        ${trackId
          ? ''
          : '<button class="edit-btn" id="edit-import-btn" title="Import a .track.json file">Import</button>'}
        <button class="edit-btn edit-btn-primary" id="edit-test-btn" title="Race the current track (in-memory edits round-trip via sessionStorage)">Test track</button>
        <button class="edit-btn" id="edit-export-btn" title="Download as .track.json (${cmdKey}+S)">Export</button>
        <button class="edit-btn" id="edit-frame-btn" title="Frame entire track (F)">Frame</button>
      </div>
    </div>

    <div class="edit-stage-row">
      <aside class="edit-side edit-side-l" id="edit-side-l">
        <div class="panel-section panel-mode" id="panel-mode" hidden>
          <h4 id="panel-mode-heading">Mode</h4>
          <div id="panel-mode-body"></div>
        </div>
        <div id="panel-track-props">
        <div class="panel-section">
          <h4>Info</h4>
          <label class="field">
            <span>Name</span>
            <input type="text" id="prop-name" autocomplete="off">
          </label>
          <label class="field">
            <span>Description</span>
            <input type="text" id="prop-desc" autocomplete="off">
          </label>
          <label class="field">
            <span>Author</span>
            <input type="text" id="prop-author" autocomplete="off">
          </label>
        </div>
        <div class="panel-section">
          <h4>Geometry</h4>
          <label class="field">
            <span>Width <span class="field-readout" id="prop-width-val"></span></span>
            <input type="range" id="prop-width" min="${WIDTH_M_MIN}" max="${WIDTH_M_MAX}" step="${WIDTH_M_STEP}">
          </label>
          <label class="field">
            <span>Alpha (smoothness) <span class="field-readout" id="prop-alpha-val"></span></span>
            <input type="range" id="prop-alpha" min="${ALPHA_MIN}" max="${ALPHA_MAX}" step="${ALPHA_STEP}">
          </label>
          <div class="field">
            <span>Direction</span>
            <div class="seg-toggle" id="prop-direction">
              <button data-dir="cw">CW</button>
              <button data-dir="ccw">CCW</button>
            </div>
          </div>
          <label class="field">
            <span>Default surface</span>
            <select id="prop-default-surface">
              <option value="grass">grass</option>
              <option value="gravel">gravel</option>
              <option value="sand">sand</option>
              <option value="tarmac">tarmac</option>
            </select>
          </label>
        </div>
        <div class="panel-section">
          <h4>Background image</h4>
          <div id="prop-bg-body"></div>
        </div>
        </div>
      </aside>

      <div class="edit-canvas-area">
        <canvas id="edit-canvas"></canvas>
        <div class="edit-overlay" id="edit-overlay">
          <button class="edit-overlay-toggle" id="edit-overlay-toggle" type="button" title="Show / hide controls" aria-expanded="true">?</button>
          <div class="edit-hint" id="edit-hint"></div>
        </div>
        <div class="edit-zoom" id="edit-zoom" title="Camera zoom — mouse-wheel to change">—</div>
        <div class="edit-loading" id="edit-loading"><span class="spinner spinner-page"></span><span>Loading track…</span></div>
      </div>

      <aside class="edit-side edit-side-r" id="edit-side-r">
        <div class="panel-section">
          <h4>Track</h4>
          <div class="panel-grid" id="panel-track-info">—</div>
        </div>
        <div class="panel-section">
          <h4>Selection</h4>
          <div id="panel-sel-body">Nothing selected</div>
        </div>
      </aside>
    </div>
  `;

  const canvas = mountEl.querySelector('#edit-canvas');
  const titleEl = mountEl.querySelector('#edit-title');
  const modEl = mountEl.querySelector('#edit-mod-indicator');
  const modesEl = mountEl.querySelector('#edit-modes');
  const hintEl = mountEl.querySelector('#edit-hint');
  const overlayEl = mountEl.querySelector('#edit-overlay');
  const overlayToggleEl = mountEl.querySelector('#edit-overlay-toggle');
  const zoomEl = mountEl.querySelector('#edit-zoom');
  const loadingEl = mountEl.querySelector('#edit-loading');
  // Import is intentionally absent when editing an existing track —
  // see the toolbar template above. The button is null in that case
  // and the listener wiring below skips it.
  const importBtn = mountEl.querySelector('#edit-import-btn');
  const exportBtn = mountEl.querySelector('#edit-export-btn');
  const frameBtn = mountEl.querySelector('#edit-frame-btn');
  const testBtn = mountEl.querySelector('#edit-test-btn');
  const panelTrackEl = mountEl.querySelector('#panel-track-info');
  const panelSelEl = mountEl.querySelector('#panel-sel-body');
  const panelModeEl = mountEl.querySelector('#panel-mode');
  const panelModeHeadingEl = mountEl.querySelector('#panel-mode-heading');
  const panelModeBodyEl = mountEl.querySelector('#panel-mode-body');
  const panelTrackPropsEl = mountEl.querySelector('#panel-track-props');
  const unitToggleEl = mountEl.querySelector('#unit-toggle');
  const surfaceIn = mountEl.querySelector('#prop-default-surface');

  // Properties sidebar inputs
  const nameIn   = mountEl.querySelector('#prop-name');
  const descIn   = mountEl.querySelector('#prop-desc');
  const authorIn = mountEl.querySelector('#prop-author');
  const widthIn  = mountEl.querySelector('#prop-width');
  const alphaIn  = mountEl.querySelector('#prop-alpha');
  const widthVal = mountEl.querySelector('#prop-width-val');
  const alphaVal = mountEl.querySelector('#prop-alpha-val');
  const dirEl    = mountEl.querySelector('#prop-direction');
  const bgBodyEl = mountEl.querySelector('#prop-bg-body');

  let editor = null;
  let cancelled = false;
  let unsubscribe = null;
  let unsubscribeCamera = null;

  // Refresh / tab-close + in-app navigation warnings while the
  // editor has unsaved changes. Same pattern as the race page: the
  // beforeunload handler covers browser-level reload/close, and the
  // router-level navigation guard covers Pit Lane / Tracks / Roster
  // / brand link clicks. Mirrors the editor's "● modified" pill —
  // they're both reading `editor.isModified()`.
  const isDirty = () => !!editor?.isModified?.();
  const onBeforeUnload = (e) => {
    if (!isDirty()) return;
    e.preventDefault();
    e.returnValue = '';
  };
  window.addEventListener('beforeunload', onBeforeUnload);
  setNavigationGuard(() => {
    if (!isDirty()) return true;
    return window.confirm('You have unsaved track changes. Leave anyway? Your edits will be lost.');
  });

  try {
    // Source resolution:
    //   sessionStorage selected track  → load via api (the manifest-served file)
    //   ?source=session                → resume in-progress edits from sessionStorage
    //                                    (set by the Test-track flow before navigating
    //                                    to the race page)
    //   (neither)                      → blank track for "+ New track"
    let track;
    if (params.source === 'session') {
      track = getEditorSessionTrack();
      if (!track) throw new Error('No session-staged track found.');
    } else if (trackId) {
      track = await getTrack(trackId);
    } else {
      track = newEmptyTrack();
    }
    if (cancelled) return;
    editor = mountEditor(canvas, track, {
      promptCalibrationDistance: (distMeters) => {
        const isImp = UNIT === 'imperial';
        const displayedNow = isImp
          ? `${(distMeters * M_TO_FT).toFixed(1)} ft`
          : `${distMeters.toFixed(1)} m`;
        const unitSuffix = isImp ? 'ft' : 'm';
        const inp = window.prompt(
          `The line measures ${displayedNow} at the current scale.\nWhat's the real-world distance, in ${unitSuffix}?`,
        );
        if (inp == null) return null;
        const n = parseFloat(inp);
        if (!isFinite(n) || n <= 0) return null;
        return isImp ? n * FT_TO_M : n;
      },
    });
    loadingEl.remove();

    const setTitle = (name) => { titleEl.textContent = name || trackId || 'Untitled track'; };
    const updateModified = () => { modEl.hidden = !editor.isModified(); };
    const updateModeUI = () => {
      modesEl.querySelectorAll('button[data-mode]').forEach(b => {
        b.classList.toggle('pill-active', b.dataset.mode === editor.state.mode);
      });
      hintEl.innerHTML = hintFor(editor.state.mode);
    };
    const updateRightPanel = () => {
      renderTrackInfo(panelTrackEl, editor.state.trackData);
      renderSelectionPanel(panelSelEl, editor);
    };
    const updateModePanel = () => {
      renderModePanel(editor, panelModeEl, panelModeHeadingEl, panelModeBodyEl);
      // Track-property panels (Info / Geometry / Background) are only
      // meaningful when authoring the track itself; hide them in other
      // modes so the mode panel has the sidebar's full attention.
      panelTrackPropsEl.hidden = editor.state.mode !== 'track';
    };
    const syncPropertiesUI = () => {
      const td = editor.state.trackData;
      // Skip overwriting the value the user is currently typing into.
      // Active sliders also stay focused during drag, so this naturally
      // doesn't fight the user.
      if (document.activeElement !== nameIn)   nameIn.value   = td.name ?? '';
      if (document.activeElement !== descIn)   descIn.value   = td.description ?? '';
      if (document.activeElement !== authorIn) authorIn.value = td.author ?? '';
      if (document.activeElement !== widthIn)  widthIn.value  = td.trackline?.width ?? 16;
      if (document.activeElement !== alphaIn)  alphaIn.value  = td.trackline?.alpha ?? 0.5;
      if (document.activeElement !== surfaceIn) surfaceIn.value = td.defaultSurface ?? 'grass';
      widthVal.textContent = fmtShort(parseFloat(widthIn.value), UNIT, 1);
      alphaVal.textContent = parseFloat(alphaIn.value).toFixed(2);
      // Direction toggle
      dirEl.querySelectorAll('button[data-dir]').forEach(b => {
        b.classList.toggle('seg-active', b.dataset.dir === (td.direction ?? 'cw'));
      });
      // Background image section — pass calibration flag so the panel
      // can show a "Cancel calibration" CTA while the 2-click flow is
      // active.
      renderBgPanel(bgBodyEl, td, !!editor.state.calibrationMode);
    };
    const updateUnitToggleUI = () => {
      unitToggleEl.querySelectorAll('button[data-unit]').forEach(b => {
        b.classList.toggle('seg-active', b.dataset.unit === UNIT);
      });
    };
    const updateZoomReadout = () => {
      const z = editor.getZoom();
      // `zoom` is pixels-per-meter (raw camera scale). Show as a
      // multiplier vs the default (4.5 px/m, what frameAll resolves to
      // for typical mid-size tracks) so "1.0×" reads as the natural
      // initial view rather than an arbitrary fractional number.
      const mult = z / 4.5;
      zoomEl.textContent = `${mult.toFixed(2)}×`;
    };

    setTitle(track.name);
    updateModified();
    updateModeUI();
    updateRightPanel();
    updateModePanel();
    syncPropertiesUI();
    updateUnitToggleUI();
    updateZoomReadout();

    unsubscribe = editor.onChange(() => {
      updateModified();
      updateRightPanel();
      updateModePanel();
      syncPropertiesUI();
    });
    unsubscribeCamera = editor.onCameraChange(updateZoomReadout);

    // ---------- bottom-overlay collapse toggle ----------
    // The controls hint defaults to collapsed so it doesn't squat on
    // the bottom of the canvas. Click the "?" pill to expand; state
    // persists across sessions in localStorage so each user gets the
    // mode they prefer without ceremony.
    const HINT_COLLAPSE_KEY = 'trackEditorHintCollapsed';
    const applyHintCollapsed = (collapsed) => {
      overlayEl.classList.toggle('edit-overlay--collapsed', collapsed);
      overlayToggleEl.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      overlayToggleEl.textContent = collapsed ? '?' : '×';
      overlayToggleEl.title = collapsed ? 'Show controls' : 'Hide controls';
    };
    let hintCollapsed = localStorage.getItem(HINT_COLLAPSE_KEY) !== '0';
    applyHintCollapsed(hintCollapsed);
    overlayToggleEl.addEventListener('click', () => {
      hintCollapsed = !hintCollapsed;
      localStorage.setItem(HINT_COLLAPSE_KEY, hintCollapsed ? '1' : '0');
      applyHintCollapsed(hintCollapsed);
    });

    // ---------- toolbar buttons ----------
    frameBtn.addEventListener('click', () => editor.frameAll());
    exportBtn.addEventListener('click', () => editor.exportTrack());
    importBtn?.addEventListener('click', () => importTrackFlow(editor, setTitle));
    testBtn.addEventListener('click', () => {
      const td = editor.state.trackData;
      if ((td.trackline?.waypoints?.length ?? 0) < 3) {
        alert('Add at least 3 trackline waypoints before testing.');
        return;
      }
      setEditorSessionTrack(td);
      // Test track preserves edits via sessionStorage, so skip the
      // unsaved-changes prompt — the user explicitly chose to test.
      clearNavigationGuard();
      window.location.hash = '#/race?source=session';
    });

    modesEl.addEventListener('click', e => {
      const btn = e.target.closest('button[data-mode]');
      if (!btn) return;
      editor.setMode(btn.dataset.mode);
      updateModeUI();
      updateModePanel();
    });

    // Mode + selection panel button + slider delegation. The wall /
    // patch settings render into the right-side Selection panel but
    // their list/CTAs render into the left mode panel, so the same
    // delegated handler runs on both containers. Targets are recreated
    // on every render so we delegate from the persistent containers.
    const handlePanelClick = (e) => {
      const btn = e.target.closest('button[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;
      const wallIdx = btn.dataset.wall != null ? Number(btn.dataset.wall) : null;
      const patchIdx = btn.dataset.patch != null ? Number(btn.dataset.patch) : null;

      // ---- waypoint selection (right panel, track + pit-lane modes) ----
      if (action === 'sharp') { editor.toggleSharpOnSelected(); return; }
      if (action === 'delete') { editor.deleteSelected(); return; }
      if (action === 'wp-reset-width') { editor.setSelectedWidth(null); return; }

      // ---- walls ----
      if (action === 'wall-new')        editor.createWall();
      else if (action === 'wall-activate' && wallIdx != null) editor.setActiveWall(wallIdx);
      else if (action === 'wall-delete' && wallIdx != null) { e.stopPropagation(); editor.deleteWall(wallIdx); }
      else if (action === 'wall-toggle-closed') {
        const w = editor.state.trackData.walls?.[editor.state.activeWallIdx];
        if (w) editor.updateActiveWall({ closed: !w.closed });
      }

      // ---- patches (surfaces) ----
      else if (action === 'patch-new')   editor.createPatch();
      else if (action === 'patch-activate' && patchIdx != null) editor.setActivePatch(patchIdx);
      else if (action === 'patch-delete' && patchIdx != null) { e.stopPropagation(); editor.deletePatch(patchIdx); }
      else if (action === 'patch-toggle-closed') {
        const p = editor.state.trackData.surfacePatches?.[editor.state.activePatchIdx];
        if (p) editor.updateActivePatch({ closed: !(p.closed !== false) });
      }
      else if (action === 'patch-set-surface') {
        editor.updateActivePatch({ surface: btn.dataset.surface });
      }
      else if (action === 'patch-set-curb-color') {
        editor.updateActivePatch({ curbColor: btn.dataset.curbColor });
      }
      else if (action === 'patch-set-tarmac-color') {
        editor.updateActivePatch({ tarmacColor: btn.dataset.tarmacColor });
      }
      else if (action === 'patch-set-endcap') {
        editor.updateActivePatch({ endCap: btn.dataset.endcap });
      }

      // ---- markers ----
      else if (action === 'marker-activate')         editor.setActiveMarker(btn.dataset.marker);
      else if (action === 'marker-enable-startline') editor.enableStartLine();
      else if (action === 'marker-disable-startline') { e.stopPropagation(); editor.disableStartLine(); }
      else if (action === 'marker-enable-sectors')   editor.enableSectors();
      else if (action === 'marker-disable-sectors')  editor.disableSectors();

      // ---- pit lane ----
      else if (action === 'pitlane-enable')  editor.enablePitLane();
      else if (action === 'pitlane-disable') editor.disablePitLane();
      else if (action === 'pitlane-toggle-marker') editor.setActivePitMarker(btn.dataset.marker);
      else if (action === 'pitlane-disable-sf') { e.stopPropagation(); editor.disablePitStartFinish(); }
      else if (action === 'pitlane-disable-zone') {
        e.stopPropagation();
        editor.disablePitSpeedZoneMarker(btn.dataset.marker);
      }
      else if (action === 'pitlane-box-side') {
        const side = btn.dataset.side;
        editor.updatePitLane({ boxSide: side === 'none' ? null : side });
      }

      // ---- racing line ----
      else if (action === 'racingline-enable')  editor.enableRacingLine();
      else if (action === 'racingline-disable') editor.disableRacingLine();
      else if (action === 'racingline-auto')    editor.autoGenerateRacingLine();

      // ---- labels ----
      else if (action === 'label-place-corner') editor.beginPlaceCorner();
      else if (action === 'label-place-area')   editor.beginPlaceArea();
      else if (action === 'label-cancel-pending') editor.cancelPendingLabel();
      else if (action === 'label-activate' && btn.dataset.label != null) {
        editor.setActiveLabel(Number(btn.dataset.label));
      }
      else if (action === 'label-delete' && btn.dataset.label != null) {
        e.stopPropagation();
        editor.deleteLabel(Number(btn.dataset.label));
      }
    };
    // Slider IDs are unique on the page so we look up the readout via
    // document.getElementById — works regardless of which panel the
    // slider was rendered into.
    const setReadout = (id, text) => {
      const out = document.getElementById(id);
      if (out) out.textContent = text;
    };
    const handlePanelInput = (e) => {
      const id = e.target.id;
      // Label text is a string field — handle it before the
      // parseFloat() default to avoid NaN'ing the numeric branches.
      if (id === 'label-text') {
        editor.updateActiveLabelLive({ text: e.target.value });
        return;
      }
      const v = parseFloat(e.target.value);
      if (id === 'wp-width') {
        editor.setSelectedWidthLive(v);
        setReadout('wp-width-val', fmtShort(v, UNIT, 1));
      } else if (id === 'wall-thickness') {
        editor.updateActiveWallLive({ thickness: v });
        setReadout('wall-thickness-val', fmtShort(v, UNIT, 2));
      } else if (id === 'wall-alpha') {
        editor.updateActiveWallLive({ alpha: v });
        setReadout('wall-alpha-val', v.toFixed(2));
      } else if (id === 'patch-thickness') {
        editor.updateActivePatchLive({ thickness: v });
        setReadout('patch-thickness-val', fmtShort(v, UNIT, 2));
      } else if (id === 'patch-alpha') {
        editor.updateActivePatchLive({ alpha: v });
        setReadout('patch-alpha-val', v.toFixed(2));
      } else if (id === 'pl-width') {
        editor.updatePitLaneLive({ width: v });
        setReadout('pl-width-val', fmtShort(v, UNIT, 1));
      } else if (id === 'pl-alpha') {
        editor.updatePitLaneLive({ alpha: v });
        setReadout('pl-alpha-val', v.toFixed(2));
      } else if (id === 'pl-speed') {
        // slider stores m/s directly; display converts via fmtSpeed.
        editor.updatePitLaneLive({ speedLimit: v });
        setReadout('pl-speed-val', fmtSpeed(v, UNIT));
      } else if (id === 'rl-alpha') {
        editor.updateRacingLineLive({ alpha: v });
        setReadout('rl-alpha-val', v.toFixed(2));
      }
    };
    const handlePanelChange = (e) => {
      const id = e.target.id;
      if (id === 'label-text') {
        // String field — text is committed on blur (change) so the
        // history doesn't fragment per keystroke.
        editor.commitHistory();
        return;
      }
      if (id === 'wp-width' || id === 'wall-thickness' || id === 'wall-alpha' ||
          id === 'patch-thickness' || id === 'patch-alpha' ||
          id === 'pl-width' || id === 'pl-alpha' || id === 'pl-speed' ||
          id === 'rl-alpha') {
        editor.commitHistory();
      }
    };
    for (const target of [panelModeBodyEl, panelSelEl]) {
      target.addEventListener('click',  handlePanelClick);
      target.addEventListener('input',  handlePanelInput);
      target.addEventListener('change', handlePanelChange);
    }

    // ---------- properties sidebar wiring ----------
    nameIn.addEventListener('change', e => {
      editor.applyChange(td => td.name = e.target.value);
    });
    descIn.addEventListener('change', e => {
      editor.applyChange(td => td.description = e.target.value);
    });
    authorIn.addEventListener('change', e => {
      editor.applyChange(td => td.author = e.target.value);
    });

    widthIn.addEventListener('input', e => {
      const v = parseFloat(e.target.value);
      editor.applyChangeLive(td => { td.trackline.width = v; });
      widthVal.textContent = fmtShort(v, UNIT, 1);
    });
    widthIn.addEventListener('change', () => editor.commitHistory());

    alphaIn.addEventListener('input', e => {
      const v = parseFloat(e.target.value);
      editor.applyChangeLive(td => { td.trackline.alpha = v; });
      alphaVal.textContent = v.toFixed(2);
    });
    alphaIn.addEventListener('change', () => editor.commitHistory());

    dirEl.addEventListener('click', e => {
      const btn = e.target.closest('button[data-dir]');
      if (!btn) return;
      const dir = btn.dataset.dir;
      if (editor.state.trackData.direction === dir) return;
      editor.applyChange(td => { td.direction = dir; });
    });

    surfaceIn.addEventListener('change', e => {
      editor.applyChange(td => { td.defaultSurface = e.target.value; });
    });

    unitToggleEl.addEventListener('click', e => {
      const btn = e.target.closest('button[data-unit]');
      if (!btn) return;
      const u = btn.dataset.unit;
      if (u !== 'imperial' && u !== 'metric') return;
      if (UNIT === u) return;
      UNIT = u;
      try { localStorage.setItem(UNIT_STORAGE_KEY, u); } catch (_) {}
      // Re-sync everything that displays values.
      updateRightPanel();
      updateModePanel();
      syncPropertiesUI();
      updateUnitToggleUI();
    });

    bgBodyEl.addEventListener('click', e => {
      const action = e.target.closest('button')?.dataset.action;
      if (action === 'bg-upload')   triggerBgUpload(editor);
      else if (action === 'bg-remove') editor.applyChange(td => { td.backgroundImage = null; });
      else if (action === 'bg-calibrate') {
        if (editor.state.calibrationMode) editor.cancelCalibration();
        else editor.beginCalibration();
      }
    });
    bgBodyEl.addEventListener('input', e => {
      const id = e.target.id;
      const v = parseFloat(e.target.value);
      if (id === 'prop-bg-opacity') {
        editor.updateBackgroundLive({ opacity: v });
        const out = bgBodyEl.querySelector('#prop-bg-opacity-val');
        if (out) out.textContent = `${Math.round(v * 100)}%`;
      } else if (id === 'prop-bg-rotation') {
        editor.updateBackgroundLive({ rotation: v * Math.PI / 180 });
        const out = bgBodyEl.querySelector('#prop-bg-rot-val');
        if (out) out.textContent = `${v.toFixed(1)}°`;
      }
    });
    bgBodyEl.addEventListener('change', e => {
      const id = e.target.id;
      const v = parseFloat(e.target.value);
      if (id === 'prop-bg-opacity' || id === 'prop-bg-rotation') {
        editor.commitHistory();
      } else if (id === 'prop-bg-cx') {
        const meters = UNIT === 'imperial' ? v * FT_TO_M : v;
        if (isFinite(meters)) editor.updateBackground({ centerX: meters });
      } else if (id === 'prop-bg-cy') {
        const meters = UNIT === 'imperial' ? v * FT_TO_M : v;
        if (isFinite(meters)) editor.updateBackground({ centerY: meters });
      } else if (id === 'prop-bg-mpp') {
        if (isFinite(v) && v > 0) editor.updateBackground({ metersPerPixel: v });
      }
    });
  } catch (err) {
    loadingEl.textContent = `Failed to load track: ${err.message}`;
    loadingEl.classList.add('error');
  }

  return () => {
    cancelled = true;
    unsubscribe?.();
    unsubscribeCamera?.();
    editor?.unmount();
    window.removeEventListener('beforeunload', onBeforeUnload);
    clearNavigationGuard();
  };
}

// ---------- helpers ----------

function importTrackFlow(editor, setTitle) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.json,application/json';
  input.addEventListener('change', e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const data = JSON.parse(ev.target.result);
        const { track: parsed } = editor.parseTrack(data);
        editor.loadTrack(parsed);
        setTitle(parsed.name);
      } catch (err) {
        alert(`Failed to load track: ${err.message}`);
      }
    };
    reader.readAsText(file);
  });
  input.click();
}

function triggerBgUpload(editor) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.addEventListener('change', e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const dataUrl = ev.target.result;
      // Default scale 0.5 m/px — typical when starting from a satellite
      // capture; calibrate-to-scale lands in 4e for tighter accuracy.
      editor.applyChange(td => {
        td.backgroundImage = {
          dataUrl,
          centerX: 0,
          centerY: 0,
          metersPerPixel: td.backgroundImage?.metersPerPixel ?? 0.5,
          rotation: td.backgroundImage?.rotation ?? 0,
          opacity: td.backgroundImage?.opacity ?? 0.6,
        };
      });
    };
    reader.readAsDataURL(file);
  });
  input.click();
}

function renderBgPanel(el, track, calibrationActive) {
  const bg = track.backgroundImage;
  if (!bg) {
    el.innerHTML = `
      <p class="muted small">Upload an aerial / satellite reference image. The track sits on top of it.</p>
      <button class="panel-btn" data-action="bg-upload">Upload image</button>
    `;
    return;
  }
  const rotDeg = ((bg.rotation ?? 0) * 180 / Math.PI);
  // Position is stored in meters; the inputs operate in user units.
  const cx = bg.centerX ?? 0;
  const cy = bg.centerY ?? 0;
  const cxDisp = (UNIT === 'imperial' ? cx * M_TO_FT : cx).toFixed(1);
  const cyDisp = (UNIT === 'imperial' ? cy * M_TO_FT : cy).toFixed(1);
  const unitSuffix = UNIT === 'imperial' ? 'ft' : 'm';
  el.innerHTML = `
    <label class="field">
      <span>Opacity <span class="field-readout" id="prop-bg-opacity-val">${Math.round((bg.opacity ?? 0.6) * 100)}%</span></span>
      <input type="range" id="prop-bg-opacity" min="0" max="1" step="0.05" value="${bg.opacity ?? 0.6}">
    </label>
    <label class="field">
      <span>Rotation <span class="field-readout" id="prop-bg-rot-val">${rotDeg.toFixed(1)}°</span></span>
      <input type="range" id="prop-bg-rotation" min="-180" max="180" step="0.5" value="${rotDeg}">
    </label>
    <div class="field" style="flex-direction:row; align-items:end; gap:6px">
      <label style="flex:1">
        <span class="field-label-x">Center X (${unitSuffix})</span>
        <input type="number" step="any" id="prop-bg-cx" value="${cxDisp}">
      </label>
      <label style="flex:1">
        <span class="field-label-x">Center Y (${unitSuffix})</span>
        <input type="number" step="any" id="prop-bg-cy" value="${cyDisp}">
      </label>
    </div>
    <div class="field" style="flex-direction:row; align-items:end; gap:6px">
      <label style="flex:1">
        <span class="field-label-x">Scale (m/px)</span>
        <input type="number" step="any" min="0.0001" id="prop-bg-mpp" value="${(bg.metersPerPixel ?? 0.5).toFixed(4)}">
      </label>
      <button class="panel-btn" data-action="bg-calibrate" style="flex:1; margin-top:0${calibrationActive ? '; background:var(--warning); border-color:var(--warning); color:var(--bg-base)' : ''}">${calibrationActive ? 'Cancel' : 'Calibrate'}</button>
    </div>
    ${calibrationActive ? '<p class="muted small">Click two points on the image at a known real-world distance (e.g., a straight section of track), then enter the real distance in the prompt.</p>' : ''}
    <div class="panel-btn-row">
      <button class="panel-btn" data-action="bg-upload">Replace</button>
      <button class="panel-btn panel-btn-danger" data-action="bg-remove">Remove</button>
    </div>
  `;
}

function computeLength(track) {
  const wps = track?.trackline?.waypoints ?? [];
  if (wps.length < 3) return 0;
  const dense = densifyClosedLoop(wps, 12, track.trackline.alpha ?? 0.5);
  let length = 0;
  for (let i = 0; i < dense.length; i++) {
    const a = dense[i], b = dense[(i + 1) % dense.length];
    length += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return length;
}

function computeBounds(track) {
  const wps = track?.trackline?.waypoints ?? [];
  if (wps.length < 1) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const wp of wps) {
    if (wp.x < minX) minX = wp.x;
    if (wp.y < minY) minY = wp.y;
    if (wp.x > maxX) maxX = wp.x;
    if (wp.y > maxY) maxY = wp.y;
  }
  return { width: maxX - minX, height: maxY - minY };
}

function renderTrackInfo(el, track) {
  const wps = track?.trackline?.waypoints ?? [];
  const bounds = computeBounds(track);
  const boundsLabel = bounds
    ? `${fmtShort(bounds.width, UNIT, 0)} × ${fmtShort(bounds.height, UNIT, 0)}`
    : '—';
  const len = computeLength(track);
  const lengthLabel = len > 0 ? fmtLong(len, UNIT) : '—';
  el.innerHTML = `
    <span class="l">waypoints</span><span class="v">${wps.length}</span>
    <span class="l">walls</span><span class="v">${(track.walls || []).length}</span>
    <span class="l">patches</span><span class="v">${(track.surfacePatches || []).length}</span>
    <span class="l">bounds</span><span class="v">${boundsLabel}</span>
    <span class="l">length</span><span class="v">${lengthLabel}</span>
  `;
}

function renderSelectionPanel(el, editor) {
  const { state } = editor;
  const sel = state.selectedIndices;

  // Walls mode: surface the active wall's settings here so the wall
  // list (left panel) and its editable properties (right panel) sit
  // in the same visual zones as track-mode waypoints. When a vertex
  // inside the active wall is selected, the waypoint-selection block
  // appears below the wall settings — same controls track-mode users
  // are familiar with, minus the width override (walls have no
  // per-vertex width).
  if (state.mode === 'walls') {
    const walls = state.trackData.walls || [];
    const active = walls[state.activeWallIdx];
    if (!active) {
      el.innerHTML = walls.length === 0
        ? '<span class="muted">No walls yet — use + New wall on the left.</span>'
        : '<span class="muted">Click a wall (in the list or on the canvas) to edit its settings.</span>';
      return;
    }
    el.innerHTML = renderActiveWallSettings(active, state.activeWallIdx)
      + waypointSelectionBlock(active.waypoints, sel, { labelPrefix: 'WP', includeWidth: false });
    return;
  }

  // Surfaces mode: same idea — active patch settings + waypoint
  // selection details when a vertex of the active patch is selected.
  if (state.mode === 'surfaces') {
    const patches = state.trackData.surfacePatches || [];
    const active = patches[state.activePatchIdx];
    if (!active) {
      el.innerHTML = patches.length === 0
        ? '<span class="muted">No patches yet — use + New patch on the left.</span>'
        : '<span class="muted">Click a patch (in the list or on the canvas) to edit its settings.</span>';
      return;
    }
    el.innerHTML = renderActivePatchSettings(active, state.activePatchIdx)
      + waypointSelectionBlock(active.polygon, sel, { labelPrefix: 'V', includeWidth: false });
    return;
  }

  // Labels mode: surface the active label's editable text + delete
  // here, mirroring how walls/surfaces show entity-level settings on
  // the right.
  if (state.mode === 'labels') {
    const labels = state.trackData.labels || [];
    const idx = state.activeLabelIdx;
    const active = idx >= 0 ? labels[idx] : null;
    if (!active) {
      el.innerHTML = labels.length === 0
        ? '<span class="muted">No labels yet — use + Add corner or + Add area on the left.</span>'
        : '<span class="muted">Click a label (in the list or on the canvas) to edit it.</span>';
      return;
    }
    const isCorner = active.kind === 'corner';
    const positionLabel = isCorner
      ? `seg ${active.segmentIdx} · t=${(active.t ?? 0).toFixed(2)}`
      : `seg ${active.startSegIdx}→${active.endSegIdx}`;
    const textPlaceholder = isCorner ? 'e.g. 1, 5a, T1' : 'e.g. Thunder Valley';
    el.innerHTML = `
      <div class="badge-row">
        <span class="badge">${isCorner ? 'Corner' : 'Area'} ${idx + 1}</span>
      </div>
      <div class="panel-grid">
        <span class="l">kind</span><span class="v">${active.kind.toUpperCase()}</span>
        <span class="l">position</span><span class="v">${positionLabel}</span>
      </div>
      <label class="field" style="margin-top:10px">
        <span>${isCorner ? 'Corner number / text' : 'Area name'}</span>
        <input type="text" id="label-text" autocomplete="off"
               placeholder="${textPlaceholder}"
               value="${escapeAttr(active.text || '')}">
      </label>
      <button class="panel-btn panel-btn-danger" data-action="label-delete" data-label="${idx}">Delete ${isCorner ? 'corner' : 'area'}</button>
    `;
    return;
  }

  // Racing-line mode: waypoint selection details (no width override —
  // the racing line is a path, not a width-bearing surface). Only
  // shown when the racing line has been enabled.
  if (state.mode === 'racingline') {
    if (!state.trackData.racingLine) {
      el.innerHTML = '<span class="muted">Racing line not enabled — use + Enable racing line on the left.</span>';
      return;
    }
    const wps = state.trackData.racingLine.waypoints;
    if (sel.length === 0) {
      el.innerHTML = '<span class="muted">Nothing selected. Click a waypoint, or drag a marquee.</span>';
      return;
    }
    el.innerHTML = waypointSelectionBlock(wps, sel, { labelPrefix: 'RL·WP', includeWidth: false });
    return;
  }

  // The waypoint selection panel is meaningful in two modes: track
  // (always), and pit-lane (when in spline-edit sub-mode —
  // activePitMarker null). Other modes' selection isn't surfaced here.
  const isTrack = state.mode === 'track';
  const isPitlaneEdit = state.mode === 'pitlane'
    && !state.activePitMarker
    && state.trackData.pitLane;

  if (!isTrack && !isPitlaneEdit) {
    if (state.mode === 'pitlane') {
      el.innerHTML = '<span class="muted">Selecting a pit-marker for placement — clear it (click again) to edit pit lane waypoints.</span>';
    } else {
      el.innerHTML = `<span class="muted">Selection not editable</span>`;
    }
    return;
  }

  const wps = isTrack
    ? state.trackData.trackline.waypoints
    : state.trackData.pitLane.waypoints;
  const defaultWidth = isTrack
    ? state.trackData.trackline.width
    : (state.trackData.pitLane.width ?? 4.0);
  if (sel.length === 0) {
    el.innerHTML = '<span class="muted">Nothing selected. Click a waypoint, or drag a marquee.</span>';
    return;
  }
  el.innerHTML = waypointSelectionBlock(wps, sel, {
    labelPrefix: isTrack ? 'WP' : 'PL·WP',
    includeWidth: true,
    defaultWidth,
  });
}

// Render the waypoint-selection details block — single + multi-select
// variants both supported. Used by track / pit-lane (with width
// override) and walls / surfaces (without). Returns '' when nothing
// is selected so callers can concat unconditionally.
function waypointSelectionBlock(wps, sel, { labelPrefix, includeWidth, defaultWidth } = {}) {
  if (!sel || sel.length === 0) return '';
  if (sel.length === 1) {
    const i = sel[0];
    const wp = wps?.[i];
    if (!wp) return '';
    const sharp = !!wp.sharp;
    return `
      <hr class="panel-divider">
      <div class="badge-row"><span class="badge">${labelPrefix} ${i}</span></div>
      <div class="panel-grid">
        <span class="l">x</span><span class="v">${fmtShort(wp.x, UNIT, 2)}</span>
        <span class="l">y</span><span class="v">${fmtShort(wp.y, UNIT, 2)}</span>
        <span class="l">type</span><span class="v">${sharp ? 'SHARP' : 'SMOOTH'}</span>
      </div>
      ${includeWidth ? widthSliderBlock([wp], defaultWidth) : ''}
      <button class="panel-btn" data-action="sharp">${sharp ? 'Make smooth' : 'Make sharp'} <span class="kbd">S</span></button>
      <button class="panel-btn panel-btn-danger" data-action="delete">Delete waypoint</button>
    `;
  }
  const selectedWps = sel.map(i => wps?.[i]).filter(Boolean);
  if (selectedWps.length === 0) return '';
  const sharpCount  = selectedWps.filter(w => w.sharp).length;
  const smoothCount = selectedWps.length - sharpCount;
  return `
    <hr class="panel-divider">
    <div class="badge-row"><span class="badge">${sel.length} selected</span></div>
    <div class="panel-grid">
      <span class="l">smooth</span><span class="v">${smoothCount}</span>
      <span class="l">sharp</span><span class="v">${sharpCount}</span>
    </div>
    ${includeWidth ? widthSliderBlock(selectedWps, defaultWidth) : ''}
    <button class="panel-btn" data-action="sharp">Toggle sharp/smooth <span class="kbd">S</span></button>
    <button class="panel-btn panel-btn-danger" data-action="delete">Delete ${sel.length} waypoints</button>
  `;
}

// Width slider block — renders the slider value + readout for the
// selection. If selected waypoints have mixed widths, falls back to
// the trackline default and labels "MIXED". The Reset button removes
// the override (waypoints inherit the track-level width again).
function widthSliderBlock(selectedWps, defaultWidth) {
  const widths = selectedWps.map(w => w.width);
  const allSame = widths.every(w => w === widths[0]);
  const mixed = !allSame;
  const anyOverride = widths.some(w => w != null);
  const display = mixed ? defaultWidth : (widths[0] ?? defaultWidth);
  const labelText = mixed
    ? `${fmtShort(display, UNIT, 1)} <span class="muted">· mixed</span>`
    : (widths[0] == null
        ? `${fmtShort(display, UNIT, 1)} <span class="muted">· default</span>`
        : fmtShort(display, UNIT, 1));
  return `
    <hr class="panel-divider">
    <div class="panel-subheading">Width override</div>
    <label class="field">
      <span>Width <span class="field-readout" id="wp-width-val">${labelText}</span></span>
      <input type="range" id="wp-width" min="3" max="30" step="0.5" value="${display}">
    </label>
    <button class="panel-btn" data-action="wp-reset-width" ${anyOverride ? '' : 'disabled'}>Reset to default</button>
  `;
}

function hintFor(mode) {
  if (mode === 'track') {
    return `${cmdKey}+click empty: add waypoint &middot; click segment: insert &middot; drag: move &middot; right-click: delete &middot; S: sharp &middot; F: frame &middot; ${cmdKey}Z/${cmdKey}⇧Z: undo/redo &middot; shift/space/middle-drag: pan`;
  }
  if (mode === 'walls') {
    return `${cmdKey}+click empty: add waypoint to active wall (snaps to track edge — hold ${altKey} to bypass) &middot; click another wall to switch &middot; click segment: insert &middot; drag: move &middot; right-click: delete`;
  }
  if (mode === 'surfaces') {
    return `${cmdKey}+click empty: add vertex to active patch (curbs snap to track edge — hold ${altKey} to bypass) &middot; click another patch to switch &middot; click edge: insert &middot; drag: move &middot; right-click: delete`;
  }
  if (mode === 'markers') {
    return `Click a marker line to select it &middot; click on the track centerline to position the active marker &middot; hold ${altKey} to place over another marker (stacked markers) &middot; use the sidebar to enable optional markers`;
  }
  if (mode === 'pitlane') {
    return `Click a pit marker to select it for placement (then click main track) &middot; ${cmdKey}+click empty: add pit-lane waypoint (snaps to track edge — hold ${altKey} to bypass) &middot; click segment: insert &middot; drag: move &middot; right-click: delete`;
  }
  if (mode === 'labels') {
    return `+ Add corner / + Add area on the left, then click the track centerline to place &middot; click an existing label to select &middot; rename or delete from the right panel &middot; Esc cancels placement`;
  }
  if (mode === 'racingline') {
    return `Enable on the left, then auto-generate or hand-tune &middot; ${cmdKey}+click empty: add waypoint &middot; click segment: insert &middot; drag: move &middot; right-click: delete &middot; S: sharp &middot; shift-click: multi-select`;
  }
  return `Mode &middot; editing not yet implemented`;
}

// ---------- mode-specific left-sidebar panel ----------
function renderModePanel(editor, panelEl, headingEl, bodyEl) {
  const mode = editor.state.mode;
  if (mode === 'track') {
    panelEl.hidden = true;
    return;
  }
  panelEl.hidden = false;
  if (mode === 'walls') {
    headingEl.textContent = 'Walls';
    renderWallsModePanel(editor, bodyEl);
    return;
  }
  if (mode === 'surfaces') {
    headingEl.textContent = 'Surface patches';
    renderSurfacesModePanel(editor, bodyEl);
    return;
  }
  if (mode === 'markers') {
    headingEl.textContent = 'Markers';
    renderMarkersModePanel(editor, bodyEl);
    return;
  }
  if (mode === 'pitlane') {
    headingEl.textContent = 'Pit lane';
    renderPitLaneModePanel(editor, bodyEl);
    return;
  }
  if (mode === 'racingline') {
    headingEl.textContent = 'Racing line';
    renderRacingLineModePanel(editor, bodyEl);
    return;
  }
  if (mode === 'labels') {
    headingEl.textContent = 'Labels';
    renderLabelsModePanel(editor, bodyEl);
    return;
  }
  headingEl.textContent = 'Mode';
  bodyEl.innerHTML = '<p class="muted small">Editing for this mode lands in a follow-up sub-phase.</p>';
}

function renderLabelsModePanel(editor, el) {
  const { state } = editor;
  const labels = state.trackData.labels || [];
  const pending = state.pendingLabel;
  const corners = labels.filter(L => L.kind === 'corner');
  const areas   = labels.filter(L => L.kind === 'area');

  // Pending placement banner: tells the user what the next click does
  // and offers a Cancel button. Visible only while a placement is in
  // progress.
  let pendingHtml = '';
  if (pending) {
    let msg;
    if (pending.kind === 'corner') msg = 'Click the track to place a corner';
    else if (pending.kind === 'area' && pending.startSegIdx == null) msg = 'Click the track for area START';
    else msg = 'Click the track for area END';
    pendingHtml = `
      <div class="panel-pending">
        <div class="panel-pending-msg">${msg}</div>
        <button class="panel-btn panel-btn-compact" data-action="label-cancel-pending">Cancel</button>
      </div>
    `;
  }

  // Two CTAs side by side. Disabled while a placement is in progress
  // so the user finishes (or cancels) before starting another one.
  const dis = pending ? 'disabled' : '';
  const cornerListHtml = corners.length === 0
    ? '<p class="muted small">No corners labeled yet.</p>'
    : labels.map((L, i) => {
        if (L.kind !== 'corner') return '';
        const text = escapeAttr(L.text || '?');
        return `
          <div class="entity-row${state.activeLabelIdx === i ? ' entity-row-active' : ''}">
            <button class="entity-row-main" data-action="label-activate" data-label="${i}">
              <span class="entity-row-label">Corner ${text}</span>
              <span class="entity-row-meta">seg ${L.segmentIdx} · t=${(L.t ?? 0).toFixed(2)}</span>
            </button>
            <button class="entity-row-del" data-action="label-delete" data-label="${i}" title="Delete corner ${text}">×</button>
          </div>
        `;
      }).join('');
  const areaListHtml = areas.length === 0
    ? '<p class="muted small">No areas named yet.</p>'
    : labels.map((L, i) => {
        if (L.kind !== 'area') return '';
        const text = escapeAttr(L.text || 'Unnamed area');
        return `
          <div class="entity-row${state.activeLabelIdx === i ? ' entity-row-active' : ''}">
            <button class="entity-row-main" data-action="label-activate" data-label="${i}">
              <span class="entity-row-label">${text}</span>
              <span class="entity-row-meta">seg ${L.startSegIdx}→${L.endSegIdx}</span>
            </button>
            <button class="entity-row-del" data-action="label-delete" data-label="${i}" title="Delete area ${text}">×</button>
          </div>
        `;
      }).join('');

  el.innerHTML = `
    ${pendingHtml}
    <div class="panel-btn-row">
      <button class="panel-btn" data-action="label-place-corner" ${dis}>+ Add corner</button>
      <button class="panel-btn" data-action="label-place-area" ${dis}>+ Add area</button>
    </div>
    <div class="panel-subheading" style="margin-top:14px">Corners</div>
    <div class="entity-list">${cornerListHtml}</div>
    <div class="panel-subheading" style="margin-top:14px">Areas</div>
    <div class="entity-list">${areaListHtml}</div>
  `;
}

function renderWallsModePanel(editor, el) {
  const { state } = editor;
  const walls = state.trackData.walls || [];

  const listHtml = walls.length === 0
    ? '<p class="muted small">No walls yet. Click + New wall, then ' + cmdKey + '+click on the canvas to drop waypoints.</p>'
    : walls.map((w, i) => `
        <div class="entity-row${i === state.activeWallIdx ? ' entity-row-active' : ''}">
          <button class="entity-row-main" data-action="wall-activate" data-wall="${i}">
            <span class="entity-row-label">Wall ${i + 1}</span>
            <span class="entity-row-meta">${w.closed ? 'closed' : 'open'} &middot; ${w.waypoints.length} pt${w.waypoints.length === 1 ? '' : 's'}</span>
          </button>
          <button class="entity-row-del" data-action="wall-delete" data-wall="${i}" title="Delete wall ${i + 1}">×</button>
        </div>
      `).join('');

  // Active-wall settings live in the right-side Selection panel —
  // selecting a wall here populates that panel via renderSelectionPanel.
  el.innerHTML = `
    <button class="panel-btn" data-action="wall-new">+ New wall</button>
    <div class="entity-list" style="margin-top:10px">${listHtml}</div>
  `;
}

// Active-wall settings block — rendered into the right-side Selection
// panel so the wall list (left) and its editable properties (right)
// live in the same visual zones as track-mode waypoints.
function renderActiveWallSettings(active, idx) {
  return `
    <div class="badge-row"><span class="badge">Wall ${idx + 1}</span></div>
    <button class="panel-btn" data-action="wall-toggle-closed">${active.closed ? 'Make open' : 'Make closed'}</button>
    <label class="field">
      <span>Thickness <span class="field-readout" id="wall-thickness-val">${fmtShort(active.thickness ?? 0.5, UNIT, 2)}</span></span>
      <input type="range" id="wall-thickness" min="0.2" max="3" step="0.1" value="${active.thickness ?? 0.5}">
    </label>
    <label class="field">
      <span>Alpha (smoothness) <span class="field-readout" id="wall-alpha-val">${(active.alpha ?? 0.5).toFixed(2)}</span></span>
      <input type="range" id="wall-alpha" min="0" max="1" step="0.05" value="${active.alpha ?? 0.5}">
    </label>
  `;
}

function renderSurfacesModePanel(editor, el) {
  const { state } = editor;
  const patches = state.trackData.surfacePatches || [];

  const listHtml = patches.length === 0
    ? '<p class="muted small">No patches yet. Click + New patch, then ' + cmdKey + '+click on the canvas to drop vertices.</p>'
    : patches.map((p, i) => {
        const surface = p.surface || 'grass';
        const label = surface.toUpperCase();
        const closed = p.closed !== false;
        const meta = `${closed ? 'closed' : 'open'} &middot; ${p.polygon.length} pt${p.polygon.length === 1 ? '' : 's'}`;
        const swatch = surface === 'curb'
          ? CURB_COLOR_HEX[p.curbColor || 'red']
          : surface === 'tarmac' && p.tarmacColor
          ? TARMAC_COLOR_HEX[p.tarmacColor] || null
          : null;
        return `
          <div class="entity-row${i === state.activePatchIdx ? ' entity-row-active' : ''}">
            <button class="entity-row-main" data-action="patch-activate" data-patch="${i}">
              <span class="entity-row-label">${label}${swatch ? ` <span class="swatch" style="background:${swatch}"></span>` : ''}</span>
              <span class="entity-row-meta">${meta}</span>
            </button>
            <button class="entity-row-del" data-action="patch-delete" data-patch="${i}" title="Delete patch ${i + 1}">×</button>
          </div>
        `;
      }).join('');

  // Active-patch settings live in the right-side Selection panel —
  // selecting a patch here populates that panel via renderSelectionPanel.
  el.innerHTML = `
    <button class="panel-btn" data-action="patch-new">+ New patch</button>
    <div class="entity-list" style="margin-top:10px">${listHtml}</div>
  `;
}

// Active-patch settings block — rendered into the right-side Selection
// panel so the patch list (left) and its editable properties (right)
// live in the same visual zones as track-mode waypoints.
function renderActivePatchSettings(active, idx) {
  const closed = active.closed !== false;
  const surfacePicker = SURFACE_TYPES.map(s => `
    <button class="surface-pill${active.surface === s ? ' surface-pill-active' : ''}" data-action="patch-set-surface" data-surface="${s}">
      ${s.toUpperCase()}
    </button>
  `).join('');

  let colorBlock = '';
  if (active.surface === 'curb') {
    const swatches = CURB_COLOR_KEYS.map(k => `
      <button class="curb-swatch${(active.curbColor || 'red') === k ? ' curb-swatch-active' : ''}"
              data-action="patch-set-curb-color" data-curb-color="${k}"
              title="Curb color: ${k}"
              style="background:${CURB_COLOR_HEX[k]}"></button>
    `).join('');
    colorBlock = `
      <div class="field"><span>Curb color</span><div class="curb-swatches">${swatches}</div></div>
    `;
  } else if (active.surface === 'tarmac') {
    const tarmacKey = active.tarmacColor || 'default';
    const swatches = TARMAC_COLOR_KEYS.map(k => `
      <button class="curb-swatch${tarmacKey === k ? ' curb-swatch-active' : ''}"
              data-action="patch-set-tarmac-color" data-tarmac-color="${k}"
              title="Tarmac tint: ${k}"
              style="background:${TARMAC_COLOR_HEX[k]}"></button>
    `).join('');
    colorBlock = `
      <div class="field"><span>Tarmac tint</span><div class="curb-swatches">${swatches}</div></div>
    `;
  }

  // Curb is open-band only — closed/open toggle is hidden when surface
  // is curb to keep the UI honest. patch-ops also enforces closed=false
  // on the data side, so a curb that somehow ends up "closed" still
  // renders as a band.
  const showClosedToggle = active.surface !== 'curb';

  let thicknessBlock = '';
  if (!closed || active.surface === 'curb') {
    const isCurb = active.surface === 'curb';
    const min  = isCurb ? CURB_THICKNESS_MIN_M     : 0.5;
    const max  = isCurb ? CURB_THICKNESS_MAX_M     : 30;
    const step = isCurb ? 0.05                     : 0.5;
    const def  = isCurb ? CURB_THICKNESS_DEFAULT_M : 8.0;
    const value = active.thickness ?? def;
    thicknessBlock = `
      <label class="field">
        <span>Band thickness <span class="field-readout" id="patch-thickness-val">${fmtShort(value, UNIT, 2)}</span></span>
        <input type="range" id="patch-thickness" min="${min}" max="${max}" step="${step}" value="${value}">
      </label>
    `;
  }

  // End-cap toggle — only meaningful for non-curb open-band patches.
  // Curbs use a stripe-driven 'butt' cap that's load-bearing for the
  // chunk math; closed patches don't have ends. Unset (or any value
  // not in the set) renders as 'round', the historical default.
  let endCapBlock = '';
  if (!closed && active.surface !== 'curb') {
    const cap = active.endCap || 'round';
    const opt = (k, label) =>
      `<button data-action="patch-set-endcap" data-endcap="${k}"
               class="${cap === k ? 'seg-active' : ''}">${label}</button>`;
    endCapBlock = `
      <div class="field">
        <span>End cap</span>
        <div class="seg-toggle">
          ${opt('round',  'Round')}
          ${opt('butt',   'Flat')}
          ${opt('square', 'Square')}
        </div>
      </div>
    `;
  }

  const surfaceLabel = (active.surface || 'grass').toUpperCase();
  return `
    <div class="badge-row"><span class="badge">Patch ${idx + 1} · ${surfaceLabel}</span></div>
    <div class="field"><span>Surface</span><div class="surface-pills">${surfacePicker}</div></div>
    ${colorBlock}
    ${showClosedToggle ? `<button class="panel-btn" data-action="patch-toggle-closed">${closed ? 'Make open band' : 'Make closed polygon'}</button>` : ''}
    ${thicknessBlock}
    ${endCapBlock}
    <label class="field">
      <span>Alpha (smoothness) <span class="field-readout" id="patch-alpha-val">${(active.alpha ?? 0.5).toFixed(2)}</span></span>
      <input type="range" id="patch-alpha" min="0" max="1" step="0.05" value="${active.alpha ?? 0.5}">
    </label>
  `;
}

function renderMarkersModePanel(editor, el) {
  const { state } = editor;
  const td = state.trackData;
  const active = state.activeMarker;

  const markerRow = (name, position, deletable) => {
    const isActive = active === name;
    const meta = position
      ? `seg ${position.segmentIdx} &middot; t=${(position.t ?? 0).toFixed(2)}`
      : 'not placed';
    const delBtn = deletable
      ? `<button class="entity-row-del" data-action="marker-disable-${name === 'startLine' ? 'startline' : ''}" title="Remove ${MARKER_LABELS[name]}">×</button>`
      : '';
    return `
      <div class="entity-row${isActive ? ' entity-row-active' : ''}">
        <button class="entity-row-main" data-action="marker-activate" data-marker="${name}">
          <span class="entity-row-label">${MARKER_LABELS[name]}</span>
          <span class="entity-row-meta">${meta}</span>
        </button>
        ${delBtn}
      </div>
    `;
  };

  let html = '<div class="entity-list">';
  // S/F is required — always present, never deletable.
  html += markerRow('startFinish', td.startFinish, false);

  // Start line — optional.
  if (td.startLine) {
    html += markerRow('startLine', td.startLine, true);
  } else {
    html += '<button class="panel-btn" data-action="marker-enable-startline">+ Add start line</button>';
  }

  // Sectors — pair-managed.
  if (Array.isArray(td.sectors) && td.sectors.length === 2) {
    html += markerRow('sector0', td.sectors[0], false);
    html += markerRow('sector1', td.sectors[1], false);
    html += '<button class="panel-btn panel-btn-danger" data-action="marker-disable-sectors" style="margin-top:6px">× Remove sector boundaries</button>';
  } else {
    html += '<button class="panel-btn" data-action="marker-enable-sectors">+ Add sector boundaries</button>';
  }
  html += '</div>';

  if (active) {
    html += `<p class="muted small" style="margin-top:10px">Click on the track to position <strong>${MARKER_LABELS[active]}</strong>.</p>`;
  } else {
    html += '<p class="muted small" style="margin-top:10px">Click a marker above to select it, then click on the track to position it.</p>';
  }

  el.innerHTML = html;
}

function renderPitLaneModePanel(editor, el) {
  const { state } = editor;
  const pl = state.trackData.pitLane;

  if (!pl) {
    el.innerHTML = `
      <p class="muted small">No pit lane authored. Add one to create the spline + entry/exit markers.</p>
      <button class="panel-btn" data-action="pitlane-enable">+ Enable pit lane</button>
    `;
    return;
  }

  const speedMs = pl.speedLimit ?? 22.2;
  const activePit = state.activePitMarker;

  const markerRow = (name, label, deleteAction) => {
    const m = pl[name];
    const placed = !!m;
    const isActive = activePit === name;
    const meta = placed
      ? `seg ${m.segmentIdx} &middot; t=${(m.t ?? 0).toFixed(2)}`
      : 'not placed';
    const delBtn = deleteAction && placed
      ? `<button class="entity-row-del" data-action="${deleteAction}" data-marker="${name}" title="Remove ${label}">×</button>`
      : '';
    return `
      <div class="entity-row${isActive ? ' entity-row-active' : ''}">
        <button class="entity-row-main" data-action="pitlane-toggle-marker" data-marker="${name}">
          <span class="entity-row-label">${label}</span>
          <span class="entity-row-meta">${meta}</span>
        </button>
        ${delBtn}
      </div>
    `;
  };

  // Mode-aware placement hint based on which pit marker is active.
  let placeHint;
  if (activePit === 'entry')                 placeHint = 'Click on the <strong>main track</strong> to place pit entry.';
  else if (activePit === 'exit')             placeHint = 'Click on the <strong>main track</strong> to place pit exit.';
  else if (activePit === 'startFinish')      placeHint = 'Click on the <strong>pit lane spline</strong> to place pit S/F.';
  else if (activePit === 'speedZoneStart')   placeHint = 'Click on the <strong>pit lane spline</strong> to mark where the speed limit BEGINS.';
  else if (activePit === 'speedZoneEnd')     placeHint = 'Click on the <strong>pit lane spline</strong> to mark where the speed limit ENDS.';
  else                                        placeHint = `Click a marker above to start placing it. Or ${cmdKey}+click on the canvas to add pit lane spline waypoints.`;

  el.innerHTML = `
    <label class="field">
      <span>Width <span class="field-readout" id="pl-width-val">${fmtShort(pl.width ?? 4.0, UNIT, 1)}</span></span>
      <input type="range" id="pl-width" min="3" max="12" step="0.5" value="${pl.width ?? 4.0}">
    </label>
    <label class="field">
      <span>Alpha <span class="field-readout" id="pl-alpha-val">${(pl.alpha ?? 0.5).toFixed(2)}</span></span>
      <input type="range" id="pl-alpha" min="0" max="1" step="0.05" value="${pl.alpha ?? 0.5}">
    </label>
    <label class="field">
      <span>Speed limit <span class="field-readout" id="pl-speed-val">${fmtSpeed(speedMs, UNIT)}</span></span>
      <input type="range" id="pl-speed" min="11.18" max="33.53" step="1.39" value="${speedMs}">
    </label>

    <hr class="panel-divider">
    <div class="panel-subheading">Entry / exit markers</div>
    <div class="entity-list">
      ${markerRow('entry', 'Pit entry')}
      ${markerRow('exit',  'Pit exit')}
    </div>

    <hr class="panel-divider">
    <div class="panel-subheading">Speed-limit zone</div>
    <div class="entity-list">
      ${markerRow('speedZoneStart', 'Speed limit begins', 'pitlane-disable-zone')}
      ${markerRow('speedZoneEnd',   'Speed limit ends',   'pitlane-disable-zone')}
    </div>
    <p class="muted small" style="margin-top:6px">Limits the AI to the pit speed limit between these gates. Without them, the limit defaults to the full pit lane spline (excluding the entry / exit ribbons).</p>

    <hr class="panel-divider">
    <div class="panel-subheading">Pit S/F (lap timing on pit-out)</div>
    <div class="entity-list">
      ${markerRow('startFinish', 'Pit S/F line', 'pitlane-disable-sf')}
    </div>
    <p class="muted small" style="margin-top:6px">Optional. When set, crossing this line on the pit lane counts as completing a lap (the main S/F can't reach into the pit lane through the wall).</p>

    <hr class="panel-divider">
    <div class="panel-subheading">Pit boxes</div>
    <div class="seg-row" style="display:flex;gap:6px">
      <button class="panel-btn ${pl.boxSide === 'left'  ? 'panel-btn-on' : ''}" data-action="pitlane-box-side" data-side="left">◀ Left</button>
      <button class="panel-btn ${pl.boxSide === 'right' ? 'panel-btn-on' : ''}" data-action="pitlane-box-side" data-side="right">Right ▶</button>
      <button class="panel-btn ${pl.boxSide == null     ? 'panel-btn-on' : ''}" data-action="pitlane-box-side" data-side="none">None</button>
    </div>
    <p class="muted small" style="margin-top:6px">Boxes are auto-generated along the pit-speed zone, on the chosen side. Capacity is set by zone length — lengthen the speed-limit zone to fit more cars.</p>

    <p class="muted small" style="margin-top:10px">${placeHint}</p>

    <hr class="panel-divider">
    <button class="panel-btn panel-btn-danger" data-action="pitlane-disable">× Remove pit lane</button>
  `;
}

function renderRacingLineModePanel(editor, el) {
  const { state } = editor;
  const rl = state.trackData.racingLine;

  if (!rl) {
    el.innerHTML = `
      <p class="muted small">No racing line authored. The AI will fall back to the centerline until you add one.</p>
      <button class="panel-btn" data-action="racingline-enable">+ Enable racing line</button>
      <p class="muted small" style="margin-top:8px">Once enabled, you can <strong>Auto-generate</strong> a starting point from the track shape, or place waypoints by hand.</p>
    `;
    return;
  }

  const wpCount = rl.waypoints?.length ?? 0;
  const enoughForSpline = wpCount >= 4;

  el.innerHTML = `
    <p class="muted small">${
      enoughForSpline
        ? `${wpCount} waypoint${wpCount === 1 ? '' : 's'} authored. Drag to refine; ${cmdKey}+click on the canvas to add at end; click on the line to insert.`
        : `Need at least 4 waypoints for a smooth closed loop (currently ${wpCount}). Use <strong>Auto-generate</strong> for a starting point, or ${cmdKey}+click to drop waypoints.`
    }</p>

    <button class="panel-btn" data-action="racingline-auto" style="margin-top:8px">⚡ Auto-generate from track</button>
    <p class="muted small" style="margin-top:6px">Replaces existing waypoints. Tries to clip apexes and stay outside on flanking straights — refine afterwards.</p>

    <hr class="panel-divider">
    <label class="field">
      <span>Alpha (smoothness) <span class="field-readout" id="rl-alpha-val">${(rl.alpha ?? 0.5).toFixed(2)}</span></span>
      <input type="range" id="rl-alpha" min="0" max="1" step="0.05" value="${rl.alpha ?? 0.5}">
    </label>

    <hr class="panel-divider">
    <button class="panel-btn panel-btn-danger" data-action="racingline-disable">× Remove racing line</button>
  `;
}
